import * as React from 'react';
import { AlertTriangle, Inbox, Loader2, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api';

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('size-4 animate-spin text-ink-4', className)} aria-hidden />;
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded bg-surface-muted', className)} aria-hidden />;
}

/** Placeholder rows sized to a table, so the layout does not jump on load. */
export function TableSkeleton({ rows = 8, columns = 6 }: { rows?: number; columns?: number }) {
  return (
    <div className="divide-y divide-line" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading…</span>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-4 px-3 py-2.5">
          {Array.from({ length: columns }).map((__, colIndex) => (
            <Skeleton
              key={colIndex}
              className={cn('h-3.5', colIndex === 0 ? 'w-28' : colIndex % 3 === 0 ? 'w-16' : 'w-24')}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon,
  className,
}: {
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center gap-2 px-6 py-14 text-center', className)}>
      <span className="flex size-10 items-center justify-center rounded-full bg-surface-muted text-ink-4">
        {icon ?? <Inbox className="size-5" />}
      </span>
      <h3 className="text-sm font-semibold text-ink">{title}</h3>
      {description ? <p className="max-w-sm text-xs text-ink-3">{description}</p> : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

/** Renders an ApiError with its server-supplied detail, plus a retry affordance. */
export function ErrorState({
  error,
  onRetry,
  title,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  /** Overrides the generic heading when the caller knows what failed. */
  title?: string;
  className?: string;
}) {
  const message = error instanceof Error ? error.message : 'Something went wrong.';
  const status = error instanceof ApiError ? error.status : null;

  return (
    <div className={cn('flex flex-col items-center gap-2 px-6 py-12 text-center', className)}>
      <span className="flex size-10 items-center justify-center rounded-full bg-danger-soft text-danger">
        <AlertTriangle className="size-5" />
      </span>
      <h3 className="text-sm font-semibold text-ink">
        {status === 403
          ? 'You do not have access to this'
          : (title ?? 'Could not load this')}
      </h3>
      <p className="max-w-md text-xs text-ink-3">{message}</p>
      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry} className="mt-1">
          <RefreshCw />
          Try again
        </Button>
      ) : null}
    </div>
  );
}

/** Small inline "syncing" hint for background refetches. */
export function RefreshingHint({ visible, className }: { visible: boolean; className?: string }) {
  if (!visible) return null;
  return (
    <span className={cn('flex items-center gap-1 text-[11px] text-ink-4', className)}>
      <Loader2 className="size-3 animate-spin" />
      Updating…
    </span>
  );
}
